import { IconFont } from '@byecode/ui'
import { Button } from '@byecode/ui/components/Button'
import type {
    DataSourceAbstract,
    EVENT_VARIABLE_TYPE,
    Field,
    IndependentAction,
    NodeFieldContent,
    RecordLikeProtocol
} from '@lighthouse/core'
import type { FlowNode, VariableTree2, Workflow } from '@lighthouse/shared'
import {
    getEventOption,
    getFieldIcon,
    getFilterControllerVariableOption,
    getFormDatasourceOptions,
    getInputVariableOption,
    getOperatorRecordInnerTypeMatch,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    NodeFieldPreview,
    PAGE_LINK_OPTION,
    useAtomAction,
    useAtomData,
    WorkflowType
} from '@lighthouse/shared'
import { filterObjectUndefined } from '@lighthouse/tools'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import styled from 'styled-components'

import { availableFieldBlocksAtom, availableFilterBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

const SCxFieldItemWrapper = styled.div`
    margin-bottom: 12px;
`

export const SCxHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const SCxNodeFieldPreviewerWrapper = styled.div`
    position: relative;
    flex: 1;
    margin-top: 2px;
    cursor: pointer;
`

export const SCxLabel = styled.div`
    flex: 1;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    height: 24px;
    overflow: hidden;
`

export const SCxIcon = styled(IconFont)``

const SCxText = styled.div`
    flex: 1;
    margin-left: 6px;
    font-size: var(--font-size-normal);
    color: var(--color-black);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
`

interface FieldItemProps {
    value: NodeFieldContent
    dataSourceList: DataSourceAbstract[]
    workflowList: Workflow[]
    field: Field
    allRecords?: RecordLikeProtocol[]
    allParentNodes?: FlowNode[]
    upstreamNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    placeholder?: string
    autoHeight?: boolean
    disablePageLink?: boolean
    disableFormOption?: boolean
    disableViewOption?: boolean
    disablePageOption?: boolean
    disableInputOption?: boolean
    eventType?: EVENT_VARIABLE_TYPE
    onItemDelete?: () => void
    onChange: (value: NodeFieldContent) => void
}

export const FieldItem: React.FC<FieldItemProps> = ({
    value,
    dataSourceList,
    workflowList,
    field,
    allParentNodes,
    upstreamNodes,
    allParentActions,
    placeholder,
    autoHeight = false,
    disablePageLink,
    disableFormOption,
    disableViewOption,
    disablePageOption,
    disableInputOption,
    eventType,
    onItemDelete,
    onChange
}) => {
    const { viewDsId = '', formDsId, viewType } = useActionAdderDepParams()
    const { type } = useFlowContainerContext()
    const disabledWithVersion = useIsDisabledWithVersion()
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const isInFlow = !!type
    const isActionFlow = type !== WorkflowType.AUTOMATION && type !== WorkflowType.SUB_PROCESS

    const formDataSource = useMemo(() => {
        if (formDsId) {
            return dataSourceList.find(ds => ds.id === formDsId)
        }
    }, [dataSourceList, formDsId])

    const viewDataSource = useMemo(() => {
        if (viewDsId) {
            return dataSourceList.find(ds => ds.id === viewDsId)
        }
    }, [dataSourceList, viewDsId])

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const formOption = useMemo(
        () =>
            !disableFormOption && formDataSource && !isInFlow
                ? getFormDatasourceOptions({
                      dataSource: formDataSource
                  })
                : undefined,
        [disableFormOption, formDataSource, isInFlow]
    )

    const eventOption = useMemo(() => eventType && getEventOption(eventType, field.innerType), [eventType, field.innerType])

    const { customViewOption } = useVariableCustomViewOption()
    const viewOptions = useMemo(() => {
        if (isInFlow || disableViewOption) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({ dataSource: viewDataSource, viewType })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, disableViewOption, customViewOption, viewDataSource, viewType])

    const inputOption = useMemo(() => {
        if (isInFlow || disableInputOption) {
            return
        }
        if (availableFieldBlocksInfo.length === 0) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [isInFlow, disableInputOption, availableFieldBlocksInfo, handleMouseEnter, handleMouseLeave, dataSourceList])

    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0 || isInFlow) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFilterBlocksInfo, handleMouseEnter, handleMouseLeave, isInFlow])

    const userOption = useGetUserRecordOptions()

    const enablePageLinkOption = !disablePageLink && isActionFlow && field.innerType === 'TEXT'

    const upstreamOptions = useMemo(() => {
        if (isInFlow && allParentNodes && upstreamNodes) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes,
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        if (allParentActions) {
            return getUpstreamActionsOptions({
                workflowList,
                allParentActions,
                dataSourceList
            })
        }
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList, upstreamNodes])

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId, includeDeviceId: isActionFlow })

    return (
        <SCxFieldItemWrapper>
            <SCxHeader>
                <SCxLabel>
                    <SCxIcon type={getFieldIcon(field.id, field.type, field.innerType)} color="var(--color-gray-400)" />
                    <SCxText>{field.name}</SCxText>
                </SCxLabel>
                <Button type="text" onClick={onItemDelete} icon={<SCxIcon type="Trash" color="var(--color-gray-400)" />} />
            </SCxHeader>
            <SCxNodeFieldPreviewerWrapper style={{ height: autoHeight ? 'auto' : 36 }}>
                <NodeFieldPreview
                    innerTypes={getOperatorRecordInnerTypeMatch(field.innerType, field.type)}
                    disabled={disabledWithVersion}
                    autoHeight={autoHeight}
                    onBlur={val => onChange({ ...value, value: val })}
                    value={value.value}
                    placeholder={placeholder}
                    field={field}
                    viewOptions={viewOptions}
                    formOption={formOption}
                    inputOption={inputOption}
                    filterOption={filterOption}
                    pageOptions={
                        isActionFlow && !disablePageOption
                            ? [enablePageLinkOption ? PAGE_LINK_OPTION : undefined, ...pageOptions].filter(filterObjectUndefined)
                            : undefined
                    }
                    upstreamOptions={upstreamOptions}
                    eventOption={eventOption}
                    userOption={userOption}
                    dataSourceList={dataSourceList}
                />
            </SCxNodeFieldPreviewerWrapper>
        </SCxFieldItemWrapper>
    )
}
